import { useEffect } from 'react';

import { useFormContext } from 'react-hook-form';

import { getRoundedNumber } from '../../../../shared/utils/mathUtils';
import {
  FOOD_FORM_NUTRIENTS,
  FoodFormNutrient,
  NUTRIENT_ROUNDING_PRECISION,
} from '../constants/foodFormNutrients';
import { FoodFormValues } from '../interfaces/FoodFormValues';
import { Measurement } from '../../../interfaces/Measurement';

/**
 * Nutritionix food only: nutrients = measurement[unit] × Unit amount, until the user edits a nutrient.
 * Nothing happens for a manual food, an invalid amount, or a unit outside the measurements.
 */
export const useRescaleNutrients = (
  nxMeasurementsByUnit: Record<string, Measurement> | null,
) => {
  const { subscribe, trigger, setValue } = useFormContext<FoodFormValues>();

  useEffect(() => {
    if (!nxMeasurementsByUnit) {
      return;
    }

    return subscribe({
      name: ['recommendedServingSize', 'amountUnit'],
      formState: { values: true, dirtyFields: true },
      callback: async ({
        values: { recommendedServingSize: amount, amountUnit, my_net_diary_bean_id },
        dirtyFields,
      }) => {
        // a typed macro wins over the measurement
        if (dirtyFields?.nutrients || !my_net_diary_bean_id) {
          return;
        }

        const base = nxMeasurementsByUnit[amountUnit];
        const isValidAmount = await trigger('recommendedServingSize');

        if (!base || !isValidAmount || !amount) {
          return;
        }

        const newNutrients = Object.fromEntries(
          FOOD_FORM_NUTRIENTS.map((nutrient) => [
            nutrient,
            getRoundedNumber(base[nutrient] * amount, NUTRIENT_ROUNDING_PRECISION),
          ]),
        ) as Record<FoodFormNutrient, number>;

        setValue('nutrients', newNutrients, { shouldDirty: false });
      },
    });
  }, [subscribe, nxMeasurementsByUnit]);
};
